import React, { useState, useCallback, useEffect } from 'react';
import { useGreyHint } from 'hamilton-web-components'
import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import debounce from 'lodash/debounce';

import { BannerType } from 'components/common';
import { validateRequiredField } from 'components/SubmissionForm/Validation';
import { fetchBrokerBranches } from 'components/SubmissionForm/Form.api';

const BrokerBranchAutocomplete = (props) => {
  const {
    fieldsState,
    validationState,
    setFieldsState,
    setValidationState,
    setBannerState,
    inputRef,
    hintText
  } = { ...props };

  const loadingTime = 1000;

  const [areBranchesLoading, setAreBranchesLoading] = useState(false);
  const [inputValue, setInputValue] = useState(' ')
  const [branches, setBranches] = useState([]);

  const { greyHintForStartAdornment } = useGreyHint({
    inputRef,
    inputValue,
    setInputValue,
    hintText,
    sx: {ml: '0.25rem', 'span': {fontFamily: "inherit", fontSize: "1rem"}}
  });

  const loadBranchesAsync = useCallback(async (brokerId, productId, companyId, filter) => {
    let response = await fetchBrokerBranches(brokerId, productId, companyId, filter);

    if (response.success) {
      setBranches(response.data);
    } else {
      setBannerState({
        show: true,
        type: BannerType.error,
        message: response.errorMessage
      });
    }
  }, [setBranches, setBannerState]);

  useEffect(() => {
    let brokerId = fieldsState.broker?.id;
    let productId = fieldsState.product?.productId;
    let companyId = fieldsState.companyId;

    if (brokerId) {
      loadBranchesAsync(brokerId, productId, companyId);
    } else {
      setBranches([]);
    }
  }, [fieldsState.broker, fieldsState.product, loadBranchesAsync]);

  const getBranchesQuery = useCallback(debounce(async (brokerId, productId, companyId, filter) => {
    await loadBranchesAsync(brokerId, productId, companyId, filter);
    setAreBranchesLoading(false);
  }, loadingTime), [setAreBranchesLoading, loadBranchesAsync]);

  const validateBranch = branchName => {
    let result = validateRequiredField("Broker Branch", branchName);
    setValidationState(previousState => ({
      ...previousState,
      fields: {
        ...previousState.fields,
        brokerBranch: result
      }
    }));
  };

  const handleChange = async (_evt, branch) => {
    const branchName = branch?.name ?? '';

    // Provide full branch object to parent
    if (props.onChange) {
      props.onChange(_evt, branch);
    }

    validateBranch(branchName);

    setFieldsState(previousState => ({
      ...previousState,
      brokerBranch: branch ? {
        id: branch.id,
        name: branchName,
        code: branch.code,
        suburb: branch.suburb,
        postalCode: branch.postalCode
      } : null,
      brokerContact: null,
      territory: null,
      ranking: null
    }));
  };

  const handleTextChange = async event => {
    let branchName = event.target?.value;
    setAreBranchesLoading(true);
    await getBranchesQuery(fieldsState.broker?.id, fieldsState.product?.productId, fieldsState.companyId, branchName);
  };

  const handleBlur = () => {
    validateBranch(fieldsState.brokerBranch?.name);
  };

  const filterOptions = (options, params) => {
    const filter = createFilterOptions({
      ignoreCase: true,
      matchFrom: 'any',
      stringify: option => option.name,
      limit: 15
    });

    const filtered = filter(options, params);
    return filtered.map(branch => ({
      id: branch.id,
      name: branch.name,
      code: branch.code,
      suburb: branch.suburb,
      postalCode: branch.postalCode
    }));
  };

  const getOptionLabel = option => option?.name ?? '';

  const renderInputElement = params => (
    <TextField
      {...params}
      inputRef={inputRef}
      required
      label="Broker Branch"
      onChange={handleTextChange}
      error={!validationState.fields.brokerBranch.valid}
      helperText={validationState.fields.brokerBranch.message}
      InputProps={{
        ...params.InputProps,
        ...greyHintForStartAdornment,
        endAdornment: (
          <>
            {areBranchesLoading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        )
      }}
    />
  );

  return <Autocomplete
    id="brokerBranch"
    disabled={!fieldsState.broker?.id}
    value={fieldsState.brokerBranch}
    onChange={handleChange}
    onBlur={handleBlur}
    filterOptions={filterOptions}
    loading={areBranchesLoading}
    clearOnBlur
    clearOnEscape
    handleHomeEndKeys
    freeSolo
    options={branches}
    getOptionLabel={getOptionLabel}
    renderOption={option => option.name}
    renderInput={renderInputElement}
    inputValue={inputValue}
    onInputChange={(_e, newValue) => setInputValue(newValue)}
  />;
};

export default BrokerBranchAutocomplete;
